import React, { useState, useEffect } from 'react'
import { ImageIcon, Plus, Trash2, X, Upload, Loader2, Eye } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import { Textarea } from '@/app/components/ui/textarea'
import { Button } from '@/app/components/ui/button'
import { Checkbox } from '@/app/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import { NewProduct, ImageFile } from '@/app/services/inventory.service'
import { FormError } from '../../ui/FormError'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import {
  calculateFinalPrice,
  calculateSalePrice,
  recalculateCosts,
  calculateTierPrices,
} from '../../../services/productCalculations.service'
import { CatalogSelect } from '../../../components/ui/CatalogSelect'
import { taxService } from '@/app/services/inventory.service'
import { productService } from '@/app/services/product.service'
import { ImagePreviewModal } from '../../ui/ImagePreviewModal'
// Helpers
// ---------------------------------------------------------------------------
const EMPTY_PRODUCT: NewProduct = {
  name: '',
  description: '',
  barcode: '',
  heroImage: null,
  galleryImages: [],
  categoryId: '',
  departmentId: '',
  productTypeId: '',
  unitId: '',
  satProductKeyId: '',
  costWithoutTaxes: '',
  costWithTaxes: '',
  profitPercentage: '30',
  taxId: '',
  batchNumber: '',
  expirationDate: '',
  requiresLot: false,
  rotationByBranch: [],
  pricingTiers: [],
}

// ─── Esquemas de validación ─────────────────────────────────────────────────
const productSchema = z.object({
  name: z.string().min(4, 'El nombre es requerido'),
  barcode: z.string().min(1, 'El código de barras es requerido'),
  description: z.string().optional(),
  heroImage: z.any().optional(),
  galleryImages: z.array(z.any()).optional(),
  categoryId: z.string().min(1, 'La categoría es requerida'),
  departmentId: z.string().min(1, 'El departamento es requerido'),
  productTypeId: z.string().min(1, 'El tipo de producto es requerido'),
  unitId: z.string().min(1, 'La unidad es requerida'),
  satProductKeyId: z.string().optional(),
  costWithoutTaxes: z.string().min(1, 'El costo es requerido'),
  costWithTaxes: z.string().min(1, 'El costo con impuestos es requerido'),
  profitPercentage: z.string().min(1, 'El porcentaje de ganancia es requerido'),
  taxId: z.string().optional(),
  batchNumber: z.string().optional(),
  expirationDate: z.string().optional(),
  rotationByBranch: z.array(
    z.object({
      sucursal_id: z.string().min(1),
      sucursal_name: z.string().optional(),
      stock_minimo: z.string().refine((val) => Number(val) >= 0, 'Mínimo debe ser >= 0'),
      stock_maximo: z.string().refine((val) => Number(val) >= 0, 'Máximo debe ser >= 0'),
    })
  ).superRefine((data, ctx) => {
    data.forEach((branch, index) => {
      if (Number(branch.stock_maximo) < Number(branch.stock_minimo)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Máximo debe ser mayor o igual al mínimo',
          path: [index, 'stock_maximo'],
        })
      }
    })
  }).optional(),
  pricingTiers: z.array(z.any()).optional(),
  requiresLot: z.boolean().optional(),
}).superRefine((data, ctx) => {
  if (data.requiresLot) {
    if (!data.batchNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El número de lote es requerido',
        path: ['batchNumber']
      });
    }
    if (!data.expirationDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La fecha de caducidad es requerida',
        path: ['expirationDate']
      });
    }
  }
});

type ProductFormValidate = z.infer<typeof productSchema>

/**
 * Crea un objeto ImageFile a partir de un archivo
 */
function createImageFile(file: File, sortOrder?: number): ImageFile {
  return { file, previewUrl: URL.createObjectURL(file), sortOrder }
}



// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
interface AddProductModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAddProduct: (product: NewProduct) => void
}

export function AddProductModal({
  open,
  onOpenChange,
  onAddProduct,
}: AddProductModalProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<ProductFormValidate>({
    resolver: zodResolver(productSchema),
    mode: 'onChange',
    defaultValues: EMPTY_PRODUCT,
    shouldFocusError: true
  })

  const { fields: pricingTiers, append: appendTier, remove: removeTier, update: updateTier } = useFieldArray({
    control,
    name: 'pricingTiers',
  })

  const { fields: rotationBranches, append: appendBranch, remove: removeBranch } = useFieldArray({
    control,
    name: 'rotationByBranch',
  })

  const { data: taxesResponse } = useQuery({
    queryKey: ['taxes', 'all'],
    queryFn: () => taxService.getTaxes({ all: true }),
  })
  const taxesList: any[] = React.useMemo(() => {
    if (!taxesResponse) return []
    // Soporte para array directo, { data: [] } o { data: { data: [] } }
    if (Array.isArray(taxesResponse)) return taxesResponse
    if (Array.isArray(taxesResponse.data)) return taxesResponse.data
    if (Array.isArray(taxesResponse.data?.data)) return taxesResponse.data.data
    return []
  }, [taxesResponse])

  // Watchers for calculated fields
  const costWithoutTaxes = watch('costWithoutTaxes')
  const costWithTaxes = watch('costWithTaxes')
  const profitPercentage = watch('profitPercentage')
  const taxId = watch('taxId')
  const productTypeId = watch('productTypeId')
  const requiresLot = watch('requiresLot')
  const heroImage = watch('heroImage')
  const galleryImages = watch('galleryImages') || []
  const tiersValues = watch('pricingTiers') || []
  const barcodeValue = watch('barcode')

  // ── Validación de Barcode en tiempo real ──────────────────────────────────
  useEffect(() => {
    if (!barcodeValue || !open) return
    const timer = setTimeout(async () => {
      try {
        const res = await productService.checkBarcode(barcodeValue)
        if (res.exists && !res.available) {
          setError('barcode', { type: 'manual', message: 'El código de barras ya está en uso' })
        } else {
          clearErrors('barcode')
        }
      } catch (err) {
        console.error('Error validating barcode:', err)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [barcodeValue, open, setError, clearErrors])

  // ---------------------------------------------------------------------------
  // STATES de Selectores y Preview
  // ---------------------------------------------------------------------------
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [searchDepartment, setSearchDepartment] = useState('')
  const [currentPageDepartment, setCurrentPageDepartment] = useState(1)
  const [searchCategory, setSearchCategory] = useState('')
  const [currentPageCategory, setCurrentPageCategory] = useState(1)
  const [searchProductType, setSearchProductType] = useState('')
  const [currentPageProductType, setCurrentPageProductType] = useState(1)
  const [searchUnit, setSearchUnit] = useState('')
  const [currentPageUnit, setCurrentPageUnit] = useState(1)
  const [searchSatProductKey, setSearchSatProductKey] = useState('')
  const [currentPageSatProductKey, setCurrentPageSatProductKey] = useState(1)
  const [searchTax, setSearchTax] = useState('')
  const [currentPageTax, setCurrentPageTax] = useState(1)

  // ---------------------------------------------------------------------------
  // Manejadores de imágenes
  // ---------------------------------------------------------------------------
  const handleHeroImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (heroImage) URL.revokeObjectURL(heroImage.previewUrl)
    setValue('heroImage', createImageFile(file), { shouldValidate: true })
    e.target.value = ''
  }

  const handleRemoveHeroImage = () => {
    if (heroImage) URL.revokeObjectURL(heroImage.previewUrl)
    setValue('heroImage', null, { shouldValidate: true })
  }

  const handleGalleryUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    const incoming = Array.from(e.target.files).map((file, i) =>
      createImageFile(file, galleryImages.length + i),
    )
    setValue('galleryImages', [...galleryImages, ...incoming], { shouldValidate: true })
    e.target.value = ''
  }

  const handleRemoveGalleryImage = (index: number) => {
    URL.revokeObjectURL(galleryImages[index].previewUrl)
    const updated = galleryImages
      .filter((_, i) => i !== index)
      .map((img, i) => ({ ...img, sortOrder: i }))
    setValue('galleryImages', updated, { shouldValidate: true })
  }

  // ---------------------------------------------------------------------------
  // Envio y Reset
  // ---------------------------------------------------------------------------
  const onSubmit = async (data: ProductFormValidate) => {
    try {
      await onAddProduct(data as NewProduct)
      if (heroImage) URL.revokeObjectURL(heroImage.previewUrl)
      galleryImages.forEach((img: ImageFile) => URL.revokeObjectURL(img.previewUrl))
      reset(EMPTY_PRODUCT)
    } catch (error) {
      console.error(error)
    }
  }

  // ---------------------------------------------------------------------------
  // Render del componente
  // ---------------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Agregar Nuevo Producto</DialogTitle>
          <DialogDescription className="sr-only">
            Formulario completo para registrar un nuevo producto en el inventario
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Información básica */}
          <fieldset className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <legend className="sr-only">Información básica</legend>
            <div className="space-y-2">
              <label htmlFor="barcode" className="text-sm font-medium text-slate-700">
                Código de Barras <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Input
                id='barcode'
                {...register('barcode')}
                placeholder="7501234567890"
                aria-invalid={!!errors.barcode}
                aria-describedby={errors.barcode ? "barcode-error" : undefined}
              />
              <FormError id="barcode-error" message={errors.barcode?.message} />
            </div>
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium text-slate-700">
                Nombre del Producto <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Input
                id='name'
                {...register('name')}
                placeholder="Ej: Paracetamol 500mg"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "name-error" : undefined}
              />
              <FormError id="name-error" message={errors.name?.message} />
            </div>
          </fieldset>

          {/* Categoría, Departamento y Tipo */}
          <fieldset className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <legend className="sr-only">Clasificación del Producto</legend>
            <div className="space-y-2">
              <label htmlFor="categoryId" className="text-sm font-medium text-slate-700">
                Categoría <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <CatalogSelect
                    catalog="categories"
                    placeholder="Seleccionar categoría"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <FormError id="categoryId-error" message={errors.categoryId?.message} />
            </div>

            <div className="space-y-2">
              <label htmlFor="departmentId" className="text-sm font-medium text-slate-700">
                Departamento <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Controller
                name="departmentId"
                control={control}
                render={({ field }) => (
                  <CatalogSelect
                    catalog="departments"
                    placeholder="Seleccionar departamento"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <FormError id="departmentId-error" message={errors.departmentId?.message} />
            </div>

            <div className="space-y-2">
              <label htmlFor="productTypeId" className="text-sm font-medium text-slate-700">
                Tipo de Producto <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Controller
                name="productTypeId"
                control={control}
                render={({ field }) => (
                  <CatalogSelect
                    catalog="product_types"
                    placeholder="Seleccionar tipo"
                    value={field.value}
                    returnObject={true}
                    onChange={(obj: any) => {
                      field.onChange(obj?.id ? String(obj.id) : '');
                      setValue('requiresLot', obj?.requires_lot === true || obj?.requires_lot === 1);
                      if (!(obj?.requires_lot === true || obj?.requires_lot === 1)) {
                        setValue('batchNumber', '');
                        setValue('expirationDate', '');
                      }
                    }}
                  />
                )}
              />
              <FormError id="productTypeId-error" message={errors.productTypeId?.message} />
            </div>
          </fieldset>

          {/* Lote y caducidad — para antibióticos y controlados */}
          <div aria-live="polite" role="status">
            {requiresLot && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="batchNumber" className="text-sm font-medium text-yellow-900">
                      Número de Lote <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
                    </label>
                    <Input
                      id='batchNumber'
                      {...register('batchNumber')}
                      placeholder="LOT-2024-XXXX"
                      aria-invalid={!!errors.batchNumber}
                      aria-describedby={errors.batchNumber ? "batchNumber-error" : undefined}
                    />
                    <p className="text-xs text-yellow-700">
                      Requerido para este tipo de producto
                    </p>
                    <FormError id="batchNumber-error" message={errors.batchNumber?.message} />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="expirationDate" className="text-sm font-medium text-yellow-900">
                      Fecha de Caducidad <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
                    </label>
                    <Input
                      id='expirationDate'
                      type="date"
                      {...register('expirationDate')}
                      aria-invalid={!!errors.expirationDate}
                      aria-describedby={errors.expirationDate ? "expirationDate-error" : undefined}
                    />
                    <FormError id="expirationDate-error" message={errors.expirationDate?.message} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Grupo IV */}
          {!requiresLot && productTypeId && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="mb-1 text-sm font-medium text-blue-900">Tipo de Producto</p>
              <p className="text-xs text-blue-700">
                Este tipo de producto no requiere número de lote ni fecha de caducidad obligatorios.
              </p>
            </div>
          )}

          {/* Unidad y clave SAT */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="unitId" className="text-sm font-medium text-slate-700">
                Unidad de Medida <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Controller
                name="unitId"
                control={control}
                render={({ field }) => (
                  <CatalogSelect
                    catalog="units"
                    placeholder="Seleccionar unidad"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <FormError id="unitId-error" message={errors.unitId?.message} />
            </div>

            <div className="space-y-2">
              <label htmlFor="satProductKeyId" className="text-sm font-medium text-slate-700">Clave SAT</label>
              <Controller
                name="satProductKeyId"
                control={control}
                render={({ field }) => (
                  <CatalogSelect
                    catalog="sat_product_keys"
                    placeholder="Buscar clave SAT"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <FormError message={errors.satProductKeyId?.message} />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor='description' className="text-sm font-medium text-slate-700">Descripción</label>
            <Textarea
              id='description'
              {...register('description')}
              placeholder="Descripción detallada del producto..."
              rows={3}
              aria-invalid={!!errors.description}
              aria-describedby={errors.description ? "description-error" : undefined}
            />
            <FormError id="description-error" message={errors.description?.message} />
          </div>

          {/* Imágenes */}
          <fieldset className="space-y-4">
            <legend className="sr-only">Imágenes del Producto</legend>
            <h4 className="text-sm font-medium text-slate-900" aria-hidden="true">Imágenes del Producto</h4>

            {/* Imagen principal */}
            <div className="flex flex-col space-y-2 items-start">
              <label htmlFor='heroImage' className="text-sm font-medium text-slate-700">
                Imagen Principal
                <span className="ml-1 text-xs font-normal text-slate-500">(opcional)</span>
              </label>

              {heroImage ? (
                <div className="group relative inline-block">
                  <img
                    src={heroImage.previewUrl}
                    alt="Imagen principal"
                    className="h-40 w-40 rounded-lg border border-slate-200 object-cover shadow-sm"
                  />
                  <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <button
                      type="button"
                      onClick={() => setPreviewImage(heroImage.previewUrl)}
                      className="rounded-full bg-white/90 p-1.5 shadow transition hover:bg-white"
                      title="Ver imagen"
                    >
                      <Eye className="h-4 w-4 text-slate-700" aria-hidden="true" />
                    </button>
                    <label
                      htmlFor="hero-image-replace"
                      className="cursor-pointer rounded-full bg-white/90 p-1.5 shadow transition hover:bg-white"
                      title="Cambiar imagen"
                    >
                      <Upload className="h-4 w-4 text-slate-700" aria-hidden="true" />
                      <span className="sr-only">Reemplazar imagen principal</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleRemoveHeroImage}
                      className="rounded-full bg-red-500/90 p-1.5 shadow transition hover:bg-red-600"
                      title="Eliminar imagen"
                      aria-label="Eliminar imagen principal"
                    >
                      <X className="h-4 w-4 text-white" aria-hidden="true" />
                    </button>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleHeroImageUpload}
                    className="hidden"
                    id="hero-image-replace"
                  />
                  <span className="mt-1 block max-w-[160px] truncate text-xs text-slate-500">
                    {heroImage.file.name}
                  </span>
                </div>
              ) : (
                <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center transition hover:border-slate-300">
                  <input
                    id="hero-image"
                    type="file"
                    accept="image/*"
                    onChange={handleHeroImageUpload}
                    className="hidden"
                  />
                  <label htmlFor="hero-image" className="cursor-pointer">
                    <ImageIcon className="mx-auto mb-2 h-10 w-10 text-slate-400" />
                    <p className="text-sm font-medium text-slate-600">
                      Haz clic para subir la imagen principal
                    </p>
                    <p className="mt-1 text-xs text-slate-500">PNG, JPG hasta 5 MB</p>
                  </label>
                </div>
              )}
            </div>

            {/* Galería */}
            <div className="space-y-2">
              <label htmlFor='galleryImages' className="text-sm font-medium text-slate-700">
                Galería de Imágenes
                <span className="ml-1 text-xs font-normal text-slate-500">
                  (opcional — múltiples)
                </span>
              </label>

              <div className="flex flex-wrap gap-3">
                {galleryImages.map((img: ImageFile, index: number) => (
                  <div key={index} className="group relative">
                    <img
                      src={img.previewUrl}
                      alt={`Galería ${index + 1}`}
                      className="h-24 w-24 rounded-lg border border-slate-200 object-cover shadow-sm"
                    />
                    <div className="absolute inset-0 flex items-center justify-center gap-1 rounded-lg bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      <button
                        type="button"
                        onClick={() => setPreviewImage(img.previewUrl)}
                        className="rounded-full bg-white/90 p-1 shadow transition hover:bg-white"
                        title="Ver imagen"
                        aria-label={`Ver imagen de galería ${index + 1}`}
                      >
                        <Eye className="h-3.5 w-3.5 text-slate-700" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveGalleryImage(index)}
                        className="rounded-full bg-red-500/90 p-1 shadow transition hover:bg-red-600"
                        title="Eliminar"
                        aria-label={`Eliminar imagen de galería ${index + 1}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-white" aria-hidden="true" />
                      </button>
                    </div>
                    <span className="absolute bottom-1 right-1 rounded bg-black/50 px-1 text-[10px] text-white">
                      {index + 1}
                    </span>
                  </div>
                ))}

                <div className="flex h-24 w-24 flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 transition hover:border-slate-300">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryUpload}
                    className="hidden"
                    id="gallery-images"
                  />
                  <label
                    htmlFor="gallery-images"
                    className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-1"
                  >
                    <Plus className="h-6 w-6 text-slate-400" />
                    <span className="text-[11px] text-slate-500">Agregar</span>
                  </label>
                </div>
              </div>

              {galleryImages.length > 0 && (
                <p className="text-xs text-slate-500">
                  {galleryImages.length} imagen
                  {galleryImages.length !== 1 ? 'es' : ''} en galería. El orden se refleja en la numeración.
                </p>
              )}
            </div>
          </fieldset>

          {/* Impuesto */}
          <fieldset className="space-y-3 rounded-lg bg-slate-50 p-4">
            <legend className="sr-only">Impuestos</legend>
            <h4 className="text-sm font-medium text-slate-900" aria-hidden="true">
              Impuesto para Precio Final al Público
            </h4>
            <p className="text-xs text-slate-600">
              Selecciona el impuesto que aplica a este producto (solo uno)
            </p>
            <div className="space-y-2">
              {taxesList.map((tax: any) => (
                <div key={tax.id} className="flex items-center gap-3">
                  <Checkbox
                    id={`apply-tax-${tax.id}`}
                    checked={taxId === String(tax.id)}
                    onCheckedChange={(checked) => {
                      const val = checked ? String(tax.id) : '';
                      const calc = recalculateCosts(costWithoutTaxes || '', taxId || '', 'tax', val, taxesList)
                      setValue('taxId', val, { shouldValidate: true })
                      if (calc.costWithTaxes !== undefined) setValue('costWithTaxes', calc.costWithTaxes)
                    }}
                  />
                  <label htmlFor={`apply-tax-${tax.id}`} className="cursor-pointer text-sm text-slate-700">
                    Agregar {tax.name} ({tax.percentage}%) al precio final
                  </label>
                </div>
              ))}
            </div>
            <FormError message={errors.taxId?.message} />
          </fieldset>

          {/* Costos */}
          <fieldset className="space-y-3 rounded-lg bg-blue-50 p-4">
            <legend className="sr-only">Costos del Producto</legend>
            <h4 className="text-sm font-medium text-blue-900" aria-hidden="true">Costo del Producto</h4>
            <p className="text-xs text-blue-700">
              Ingresa el costo con o sin impuesto; el otro se calculará automáticamente
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor='costWithoutTaxes' className="text-sm font-medium text-blue-900">
                  Costo sin Impuesto <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
                </label>
                <Input
                  id='costWithoutTaxes'
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('costWithoutTaxes', {
                    onChange: (e) => {
                      const calc = recalculateCosts(costWithoutTaxes || '', taxId || '', 'without', e.target.value, taxesList)
                      if (calc.costWithTaxes !== undefined) setValue('costWithTaxes', calc.costWithTaxes, { shouldValidate: true })
                    }
                  })}
                  placeholder="0.00"
                  aria-invalid={!!errors.costWithoutTaxes}
                  aria-describedby={errors.costWithoutTaxes ? "costWithoutTaxes-error" : undefined}
                />
                <FormError id="costWithoutTaxes-error" message={errors.costWithoutTaxes?.message} />
              </div>
              <div className="space-y-2">
                <label htmlFor='costWithTaxes' className="text-sm font-medium text-blue-900">
                  Costo con Impuesto
                </label>
                <Input
                  id='costWithTaxes'
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('costWithTaxes', {
                    onChange: (e) => {
                      const calc = recalculateCosts(costWithoutTaxes || '', taxId || '', 'with', e.target.value, taxesList)
                      if (calc.costWithoutTaxes !== undefined) setValue('costWithoutTaxes', calc.costWithoutTaxes, { shouldValidate: true })
                    }
                  })}
                  placeholder="0.00"
                  aria-invalid={!!errors.costWithTaxes}
                  aria-describedby={errors.costWithTaxes ? "costWithTaxes-error" : undefined}
                />
                <FormError id="costWithTaxes-error" message={errors.costWithTaxes?.message} />
              </div>
            </div>
            {!taxId && (
              <p className="rounded bg-orange-100 p-2 text-xs text-orange-700">
                Sin impuesto seleccionado: costo sin impuesto y con impuesto serán iguales
              </p>
            )}
          </fieldset>

          {/* Margen de utilidad y precio */}
          <div className="space-y-2">
            <label htmlFor='profitPercentage' className="text-sm font-medium text-slate-700">
              Porcentaje de Utilidad <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
            </label>
            <div className="flex items-center gap-3">
              <Input
                id='profitPercentage'
                type="number"
                min={0}
                max={500}
                step={1}
                {...register('profitPercentage')}
                placeholder="30"
                className="flex-1"
                aria-invalid={!!errors.profitPercentage}
                aria-describedby={errors.profitPercentage ? "profitPercentage-error" : undefined}
              />
              <span className="text-2xl font-semibold text-blue-700">
                {profitPercentage || 0}%
              </span>
            </div>
            <p className="text-xs text-slate-600">
              Porcentaje de ganancia sobre el costo sin impuesto
            </p>
            <FormError id="profitPercentage-error" message={errors.profitPercentage?.message} />
          </div>

          {/* Resumen de precio */}
          {costWithoutTaxes && profitPercentage && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-live="polite" role="status">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="mb-1 text-xs text-slate-600">Precio de venta base (sin imp.)</p>
                <p className="text-xl font-semibold text-slate-800">
                  ${calculateSalePrice(costWithoutTaxes, profitPercentage)}
                </p>
              </div>
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <p className="mb-1 text-xs text-green-700">Precio final al público</p>
                <p className="text-2xl font-semibold text-green-700">
                  ${calculateFinalPrice(costWithoutTaxes || '', profitPercentage || '', taxId || '', taxesList)}
                </p>
                <div className="mt-1 space-y-0.5 text-xs text-green-600">
                  <p>Base: ${calculateSalePrice(costWithoutTaxes, profitPercentage)}</p>
                  {taxId && taxesList.find((t: any) => String(t.id) === taxId) && (() => {
                    const selectedTax = taxesList.find((t: any) => String(t.id) === taxId);
                    return (
                      <p>
                        + {selectedTax.name} ({selectedTax.percentage}%): $
                        {(parseFloat(calculateSalePrice(costWithoutTaxes, profitPercentage)) * (Number(selectedTax.percentage) / 100)).toFixed(2)}
                      </p>
                    )
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Precios por cantidad */}
          <div className="space-y-3 rounded-lg border border-purple-200 bg-purple-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-purple-900">Precios por Cantidad</h5>
                <p className="mt-0.5 text-xs text-purple-700">
                  Configura descuentos o utilidades distintas según la cantidad mínima vendida
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={() =>
                  appendTier({
                    minQuantity: 1,
                    profitPercentage: 0,
                    discountPercentage: 0,
                    price: '',
                    priceWithTax: '',
                  })
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Agregar Nivel
              </Button>
            </div>

            {pricingTiers.length > 0 ? (
              <div className="space-y-2">
                {pricingTiers.map((tier, index) => {
                  const currentTierVal = tiersValues[index] || tier
                  const tierCalc = calculateTierPrices(
                    costWithoutTaxes || '',
                    parseFloat(currentTierVal.profitPercentage) || 0,
                    parseFloat(currentTierVal.discountPercentage) || 0,
                    taxId || '',
                    taxesList
                  )

                  return (
                    <div
                      key={tier.id}
                      className="grid grid-cols-5 items-end gap-2 rounded-lg bg-white p-3"
                    >
                      <div className="space-y-1">
                        <label htmlFor={`pricingTiers.${index}.minQuantity`} className="text-xs text-slate-600">Cant. mínima</label>
                        <Input
                          id={`pricingTiers.${index}.minQuantity`}
                          type="number"
                          min={1}
                          {...register(`pricingTiers.${index}.minQuantity` as const)}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor={`pricingTiers.${index}.profitPercentage`} className="text-xs text-slate-600">Utilidad %</label>
                        <Input
                          id={`pricingTiers.${index}.profitPercentage`}
                          type="number"
                          min={0}
                          max={500}
                          {...register(`pricingTiers.${index}.profitPercentage` as const)}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor={`pricingTiers.${index}.discountPercentage`} className="text-xs text-slate-600">Descuento %</label>
                        <Input
                          id={`pricingTiers.${index}.discountPercentage`}
                          type="number"
                          min={0}
                          max={100}
                          {...register(`pricingTiers.${index}.discountPercentage` as const)}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor={`pricingTiers.${index}.price`} className="text-xs text-slate-600">Precio final</label>
                        <div className="flex h-8 items-center rounded-md bg-green-50 px-2 text-xs font-medium text-green-700">
                          {tierCalc.finalPrice ? `$${tierCalc.finalPrice}` : '—'}
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          type="button"
                          onClick={() => removeTier(index)}
                          className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-600"
                          aria-label={`Eliminar nivel de precio ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="py-2 text-center text-xs text-purple-600">
                Sin niveles configurados. Haz clic en "Agregar Nivel" para comenzar.
              </p>
            )}
          </div>

          {/* Rotación por sucursal (Oculta por el momento) */}
          <fieldset className="hidden space-y-3 rounded-lg bg-slate-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-slate-900">Rotación por sucursal</h4>
                <p className="text-xs text-slate-600">
                  Configura el mínimo y máximo de stock por sucursal
                </p>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <CatalogSelect
                  catalog="sucursales"
                  onChange={(branchId: string | any) => {
                    if (typeof branchId === 'object' && branchId?.id) {
                      const alreadyExists = rotationBranches.some(b => String(b.sucursal_id) === String(branchId.id));
                      if (!alreadyExists) {
                        appendBranch({
                          sucursal_id: String(branchId.id),
                          sucursal_name: branchId.name || `Sucursal ${branchId.id}`,
                          stock_minimo: '0',
                          stock_maximo: '0'
                        });
                      }
                    }
                  }}
                  placeholder="Seleccionar sucursal..."
                  returnObject={true}
                />
              </div>
            </div>

            {rotationBranches.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-500">
                  <div className="col-span-5">Sucursal</div>
                  <div className="col-span-3">Mínimo</div>
                  <div className="col-span-3">Máximo</div>
                  <div className="col-span-1"></div>
                </div>
                {rotationBranches.map((branch, index) => (
                  <div key={branch.id} className="grid grid-cols-12 items-center gap-2 rounded bg-white p-2 border border-slate-200">
                    <div className="col-span-5 text-sm font-medium text-slate-700 truncate">
                      {watch(`rotationByBranch.${index}.sucursal_name`)}
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        min="0"
                        {...register(`rotationByBranch.${index}.stock_minimo` as const)}
                        className="h-8"
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        min="0"
                        {...register(`rotationByBranch.${index}.stock_maximo` as const)}
                        className="h-8"
                      />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => removeBranch(index)}
                        className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="col-span-12">
                      <FormError message={errors.rotationByBranch?.[index]?.stock_maximo?.message} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </fieldset>

          {/* Acciones */}
          <div className="flex gap-3 border-t border-slate-200 pt-4">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSubmitting ? 'Agregando...' : 'Agregar Producto'}
            </Button>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)} className="flex-1">
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>

      <ImagePreviewModal
        open={previewImage !== null}
        onOpenChange={(open) => !open && setPreviewImage(null)}
        imageUrl={previewImage}
      />
    </Dialog>
  )
}
